import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import FactureAchatPreviewDrawer from '@/components/FactureAchatPreviewDrawer';
import {
  Upload, FileText, Loader2, Search, Building2, Calendar, Hash,
  CheckCircle2, AlertTriangle, Receipt, ChevronRight, Camera, Mail,
  Settings2, X, Trash2, Send, CheckSquare, Square, Filter,
} from 'lucide-react';

const fmt = (n) => (typeof n === 'number' ? n : parseFloat(n) || 0).toLocaleString('fr-FR', {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
});

const STATUT_STYLE = {
  'À vérifier':      'bg-amber-100 text-amber-700',
  'Validée':         'bg-blue-100 text-blue-700',
  'Poussée Pennylane': 'bg-emerald-100 text-emerald-700',
};

export default function FacturesAchat() {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const cameraRef = useRef(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [lastScan, setLastScan] = useState(null);
  const [scanProgress, setScanProgress] = useState(null);
  // Insertion groupée (bulk upload)
  const [batching, setBatching] = useState(false);
  const [batchProgress, setBatchProgress] = useState(null);
  // Sélection multiple pour actions groupées (valider / supprimer / push Pennylane)
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(null); // 'validate'|'delete'|'push'|null
  const [pushBulkRunning, setPushBulkRunning] = useState(false);
  const [pushBulkProgress, setPushBulkProgress] = useState(null);
  const [showScanOptions, setShowScanOptions] = useState(false);
  // Par défaut: depuis le 1er avril de l'année en cours
  const [sinceDate, setSinceDate] = useState(() => {
    const y = new Date().getFullYear();
    return `${y}-04-01`;
  });
  const [includeRead, setIncludeRead] = useState(true);
  const [markRead, setMarkRead] = useState(true);
  // Filtre statut (chips) et sélection preview
  const [statutFilter, setStatutFilter] = useState('all'); // 'all' | 'À vérifier' | 'Validée' | 'Poussée Pennylane'
  const [previewFacture, setPreviewFacture] = useState(null);

  // Quand la liste change (après edit/push/delete), on rafraîchit la facture
  // affichée dans le drawer pour refléter le nouveau statut.
  useEffect(() => {
    if (!previewFacture) return;
    const fresh = items.find((f) => f.facture_achat_id === previewFacture.facture_achat_id);
    if (fresh && fresh !== previewFacture) setPreviewFacture(fresh);
    else if (!fresh) setPreviewFacture(null); // supprimée
  }, [items]);

  const loadLastScan = async () => {
    try {
      const r = await api.get('/factures-achat/scan-emails/progress');
      if (r.data && Object.keys(r.data).length) {
        setLastScan(r.data);
        if (r.data.running) {
          setScanning(true);
          setScanProgress(r.data);
        }
      }
    } catch (_) { /* admin only */ }
  };

  // Polling pendant le scan
  useEffect(() => {
    if (!scanning) return;
    const id = setInterval(async () => {
      try {
        const r = await api.get('/factures-achat/scan-emails/progress');
        const p = r.data || {};
        setScanProgress(p);
        if (!p.running) {
          setLastScan(p);
          setScanning(false);
          if (p.error) {
            toast.error(`Scan échoué : ${p.error}`, { duration: 12000 });
          } else {
            const mbStr = p.mailboxes_scanned ? ` · ${p.mailboxes_scanned} boîte(s)` : '';
            if (p.imported > 0) {
              toast.success(
                `${p.imported} facture(s) importée(s)${mbStr}`
                + (p.skipped_dup ? ` · ${p.skipped_dup} doublon(s)` : '')
                + (p.skipped_not_invoice ? ` · ${p.skipped_not_invoice} non-facture(s)` : ''),
                { duration: 12000 }
              );
              reload();
            } else {
              toast.info(
                `Aucune nouvelle facture (${p.scanned_emails || 0} email(s) scanné(s)${mbStr})`
                + (p.skipped_dup ? ` · ${p.skipped_dup} doublon(s)` : '')
                + (p.skipped_not_invoice ? ` · ${p.skipped_not_invoice} non-facture(s) ignorée(s)` : ''),
                { duration: 10000 }
              );
            }
          }
        }
      } catch (_) { /* keep polling */ }
    }, 2500);
    return () => clearInterval(id);
  }, [scanning]);

  // Polling pendant l'insertion groupée
  useEffect(() => {
    if (!batching) return;
    // Précharge au cas où une session était déjà en cours côté serveur
    (async () => {
      try {
        const r = await api.get('/factures-achat/batch/progress');
        if (r.data) setBatchProgress(r.data);
      } catch (_) { /* noop */ }
    })();
    const id = setInterval(async () => {
      try {
        const r = await api.get('/factures-achat/batch/progress');
        const p = r.data || {};
        setBatchProgress(p);
        if (!p.running) {
          setBatching(false);
          const imported = p.imported || 0;
          const failed = p.failed || 0;
          if (imported > 0 && failed === 0) {
            toast.success(`${imported} facture(s) importée(s) avec succès`, { duration: 8000 });
          } else if (imported > 0 && failed > 0) {
            toast.warning(`${imported} importée(s) · ${failed} en erreur`, { duration: 12000 });
          } else if (failed > 0) {
            toast.error(`Échec de l'import (${failed} erreur(s))`, { duration: 12000 });
          }
          reload();
        }
      } catch (_) { /* keep polling */ }
    }, 2000);
    return () => clearInterval(id);
  }, [batching]);

  // Polling pendant le push Pennylane groupé
  useEffect(() => {
    if (!pushBulkRunning) return;
    const id = setInterval(async () => {
      try {
        const r = await api.get('/factures-achat/bulk/push-pennylane/progress');
        const p = r.data || {};
        setPushBulkProgress(p);
        if (!p.running) {
          setPushBulkRunning(false);
          const pushed = p.pushed || 0;
          const failed = p.failed || 0;
          if (pushed > 0 && failed === 0) {
            toast.success(`${pushed} facture(s) poussée(s) vers Pennylane`, { duration: 10000 });
          } else if (pushed > 0 && failed > 0) {
            toast.warning(`${pushed} poussée(s) · ${failed} en erreur`, { duration: 14000 });
          } else if (failed > 0) {
            toast.error(`Échec : ${failed} erreur(s) Pennylane`, { duration: 14000 });
          }
          reload();
        }
      } catch (_) { /* keep polling */ }
    }, 2000);
    return () => clearInterval(id);
  }, [pushBulkRunning]);

  const handleScanEmails = async () => {
    if (scanning) return;
    setShowScanOptions(false);
    setScanning(true);
    setScanProgress({ running: true, step: 'Démarrage…' });
    try {
      const r = await api.post('/factures-achat/scan-emails', null, {
        params: {
          since_date: sinceDate || undefined,
          unseen_only: !includeRead,
          mark_read: markRead,
        },
      });
      if (r.data?.message) toast.info(r.data.message, { duration: 5000 });
    } catch (e) {
      setScanning(false);
      let detail = e?.response?.data?.detail;
      if (detail && typeof detail !== 'string') {
        try { detail = JSON.stringify(detail); } catch (_) { detail = String(detail); }
      }
      toast.error(detail || 'Échec du démarrage du scan IMAP', { duration: 10000 });
    }
  };

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.get('/factures-achat');
      setItems(r.data || []);
    } catch (e) {
      toast.error("Impossible de charger les factures d'achat");
    }
    setLoading(false);
  };

  useEffect(() => {
    document.title = "Factures d'achat - Reno Resolve";
    reload();
    loadLastScan();
    // Reprise du batch si une session est encore en cours côté serveur
    (async () => {
      try {
        const r = await api.get('/factures-achat/batch/progress');
        if (r.data?.running) {
          setBatchProgress(r.data);
          setBatching(true);
        }
      } catch (_) { /* noop */ }
    })();
    // Reprise du push Pennylane si une session est en cours
    (async () => {
      try {
        const r = await api.get('/factures-achat/bulk/push-pennylane/progress');
        if (r.data?.running) {
          setPushBulkProgress(r.data);
          setPushBulkRunning(true);
        }
      } catch (_) { /* noop */ }
    })();
  }, []);

  const uploadFile = async (file) => {
    if (!file) return;
    const name = (file.name || '').toLowerCase();
    const isPdf = name.endsWith('.pdf') || file.type === 'application/pdf';
    const isImage = file.type?.startsWith('image/') ||
      ['.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif'].some((ext) => name.endsWith(ext));
    if (!isPdf && !isImage) {
      toast.error('Format non supporté. Acceptés : PDF ou photo (JPG/PNG/HEIC).');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/factures-achat', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 90000,
      });
      const src = r.data.facture_achat?.source_type === 'photo' ? '📷 Photo' : '📄 PDF';
      toast.success(`${src} — OCR terminé : ${r.data.facture_achat?.fournisseur?.nom || file.name}`);
      await reload();
      navigate(`/factures-achat/${r.data.facture_achat.facture_achat_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'upload OCR", { duration: 8000 });
    }
    setUploading(false);
  };

  // Insertion groupée — lance le batch et démarre le polling
  const uploadBatch = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    // Filtrage côté client (format)
    const accepted = files.filter((f) => {
      const n = (f.name || '').toLowerCase();
      const pdf = n.endsWith('.pdf') || f.type === 'application/pdf';
      const img = f.type?.startsWith('image/') ||
        ['.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif'].some((ext) => n.endsWith(ext));
      return pdf || img;
    });
    if (!accepted.length) {
      toast.error('Aucun fichier au format accepté (PDF, JPG, PNG, HEIC).');
      return;
    }
    if (accepted.length < files.length) {
      toast.warning(`${files.length - accepted.length} fichier(s) ignoré(s) (format non supporté)`);
    }
    setBatching(true);
    setBatchProgress({ running: true, step: 'Envoi…', total: accepted.length, processed: 0, imported: 0, failed: 0 });
    try {
      const fd = new FormData();
      accepted.forEach((f) => fd.append('files', f));
      const r = await api.post('/factures-achat/batch', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000, // 5 min pour gros uploads
      });
      toast.info(r.data?.message || `${accepted.length} fichier(s) en cours…`, { duration: 5000 });
    } catch (e) {
      setBatching(false);
      setBatchProgress(null);
      toast.error(e?.response?.data?.detail || "Échec du démarrage de l'insertion groupée", { duration: 10000 });
    }
  };

  const handleFileInputChange = (e) => {
    const files = e.target.files;
    if (!files || !files.length) return;
    if (files.length === 1) {
      uploadFile(files[0]);
    } else {
      uploadBatch(files);
    }
    // Reset pour permettre une nouvelle sélection identique
    try { e.target.value = ''; } catch (_) { /* noop */ }
  };

  // -- Sélection multiple --------------------------------------------------
  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = (visibleItems) => {
    setSelectedIds((prev) => {
      const visibleIds = visibleItems.map((f) => f.facture_achat_id);
      const allSelected = visibleIds.every((id) => prev.has(id));
      const next = new Set(prev);
      if (allSelected) {
        visibleIds.forEach((id) => next.delete(id));
      } else {
        visibleIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  // -- Actions groupées ----------------------------------------------------
  const handleBulkValidate = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    setBulkActionLoading('validate');
    try {
      const r = await api.post('/factures-achat/bulk/validate', { ids });
      const { updated = 0, skipped = 0, total = ids.length } = r.data || {};
      if (updated > 0) {
        toast.success(`${updated} facture(s) validée(s)${skipped ? ` · ${skipped} ignorée(s) (déjà poussées)` : ''}`);
      } else {
        toast.info('Aucune facture validée (toutes étaient déjà poussées Pennylane)');
      }
      clearSelection();
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec de la validation groupée', { duration: 8000 });
    }
    setBulkActionLoading(null);
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    if (!window.confirm(`Supprimer définitivement ${ids.length} facture(s) ? Cette action est irréversible (les factures déjà poussées sur Pennylane seront ignorées).`)) return;
    setBulkActionLoading('delete');
    try {
      const r = await api.post('/factures-achat/bulk/delete', { ids });
      const { deleted = 0, skipped_pushed = 0 } = r.data || {};
      if (deleted > 0) {
        toast.success(`${deleted} facture(s) supprimée(s)${skipped_pushed ? ` · ${skipped_pushed} conservée(s) (déjà poussées Pennylane)` : ''}`);
      } else {
        toast.info('Aucune facture supprimée (toutes étaient déjà poussées Pennylane)');
      }
      clearSelection();
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec de la suppression groupée', { duration: 8000 });
    }
    setBulkActionLoading(null);
  };

  const handleBulkPushPennylane = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    if (!window.confirm(`Pousser ${ids.length} facture(s) vers Pennylane ?`)) return;
    setBulkActionLoading('push');
    try {
      const r = await api.post('/factures-achat/bulk/push-pennylane', { ids });
      const data = r.data || {};
      if (data.running) {
        setPushBulkRunning(true);
        setPushBulkProgress({ running: true, total: data.total, processed: 0, pushed: 0, failed: 0, step: 'Démarrage…' });
        toast.info(data.message || `Envoi de ${data.total} facture(s) démarré…`);
      } else {
        toast.info(data.message || 'Aucune facture éligible.');
      }
      clearSelection();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Échec du démarrage du push Pennylane', { duration: 10000 });
    }
    setBulkActionLoading(null);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = e.dataTransfer.files;
    if (!files || !files.length) return;
    if (files.length === 1) uploadFile(files[0]);
    else uploadBatch(files);
  };

  const filtered = items.filter((f) => {
    // Filtre statut
    if (statutFilter !== 'all' && f.statut !== statutFilter) return false;
    // Filtre recherche
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (f.numero_facture || '').toLowerCase().includes(q) ||
      (f.fournisseur?.nom || '').toLowerCase().includes(q) ||
      (f.description || '').toLowerCase().includes(q)
    );
  });

  const totalTTC = items.reduce((s, f) => s + (f.montants?.total_ttc || 0), 0);
  const nbByStatut = items.reduce((acc, f) => { acc[f.statut] = (acc[f.statut] || 0) + 1; return acc; }, {});

  // Filtre chips définitions
  const STATUT_FILTERS = [
    { key: 'all',                 label: 'Toutes',            count: items.length,                   pill: 'bg-slate-100 text-slate-700 border-slate-200', pillActive: 'bg-slate-800 text-white border-slate-800' },
    { key: 'À vérifier',          label: 'À vérifier',        count: nbByStatut['À vérifier'] || 0,       pill: 'bg-amber-50 text-amber-700 border-amber-200', pillActive: 'bg-amber-600 text-white border-amber-700' },
    { key: 'Validée',             label: 'Validées',          count: nbByStatut['Validée'] || 0,          pill: 'bg-blue-50 text-blue-700 border-blue-200',       pillActive: 'bg-blue-600 text-white border-blue-700' },
    { key: 'Poussée Pennylane',   label: 'Poussées Pennylane', count: nbByStatut['Poussée Pennylane'] || 0, pill: 'bg-emerald-50 text-emerald-700 border-emerald-200', pillActive: 'bg-emerald-600 text-white border-emerald-700' },
  ];

  return (
    <div className="space-y-5" data-testid="factures-achat-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center">
            <Receipt className="w-6 h-6 text-indigo-700" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Factures d'achat</h1>
            <p className="text-sm text-slate-500 mt-0.5">{items.length} facture(s) · {fmt(totalTTC)} € TTC cumulé</p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {STATUT_FILTERS.map((s) => {
            const isActive = statutFilter === s.key;
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => setStatutFilter(s.key)}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                  isActive ? s.pillActive : s.pill + ' hover:brightness-95'
                }`}
                data-testid={`fa-filter-${s.key}`}
              >
                {s.label}
                <span className={`px-1.5 py-0 rounded-full text-[10px] font-bold ${
                  isActive ? 'bg-white/20 text-white' : 'bg-white/60'
                }`}>
                  {s.count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Drop zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`bg-white rounded-2xl border-2 border-dashed p-8 text-center transition-all ${
          dragOver ? 'border-indigo-500 bg-indigo-50' : 'border-slate-300 hover:border-indigo-400 hover:bg-slate-50'
        }`}
        data-testid="fa-dropzone"
      >
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf,image/*"
          multiple
          onChange={handleFileInputChange}
          className="hidden"
          data-testid="fa-file-input"
        />
        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={(e) => uploadFile(e.target.files?.[0])}
          className="hidden"
          data-testid="fa-camera-input"
        />
        {uploading ? (
          <div key="uploading-state" className="flex flex-col items-center gap-2">
            <Loader2 className="w-10 h-10 text-indigo-600 animate-spin" />
            <p className="text-sm font-semibold text-slate-700">Analyse OCR en cours… (5-15 s)</p>
            <p className="text-xs text-slate-500">L'IA extrait fournisseur, montants, TVA et lignes</p>
          </div>
        ) : (
          <div key="idle-state" className="flex flex-col items-center gap-3">
            <Upload className="w-10 h-10 text-indigo-500" />
            <div>
              <p className="text-sm font-semibold text-slate-700">Glissez un (ou plusieurs) PDF/photo de facture ici</p>
              <p className="text-xs text-slate-500">PDF, JPG, PNG, HEIC · max 20 Mo/fichier · OCR par GPT-4o Vision · sélection multiple supportée</p>
            </div>
            <div className="flex items-center gap-2 mt-1">
              <Button
                type="button"
                variant="outline"
                onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}
                data-testid="fa-browse-btn"
              >
                <Upload className="w-4 h-4 mr-1.5" /> Parcourir
              </Button>
              <Button
                type="button"
                onClick={(e) => { e.stopPropagation(); cameraRef.current?.click(); }}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="fa-camera-btn"
              >
                <Camera className="w-4 h-4 mr-1.5" /> Prendre en photo
              </Button>
            </div>
            <p className="text-[10px] text-slate-400">Sur mobile, l'appareil photo s'ouvre directement ; la photo est convertie en PDF automatiquement.</p>
          </div>
        )}
      </div>

      {/* Search + scan IMAP */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher (fournisseur, n° facture, description)…"
            className="pl-9"
            data-testid="fa-search"
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            type="button"
            variant="outline"
            onClick={handleScanEmails}
            disabled={scanning}
            data-testid="fa-scan-emails-btn"
            className="border-indigo-200 text-indigo-700 hover:bg-indigo-50"
          >
            {scanning
              ? <Loader2 key="scan-loader" className="w-4 h-4 mr-1.5 animate-spin" />
              : <Mail key="scan-icon" className="w-4 h-4 mr-1.5" />}
            {scanning ? 'Scan en cours…' : 'Scanner les emails'}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setShowScanOptions((v) => !v)}
            disabled={scanning}
            data-testid="fa-scan-options-btn"
            className="text-slate-600 hover:bg-slate-100"
            title="Options de scan"
          >
            {showScanOptions
              ? <X key="scan-opts-close" className="w-4 h-4" />
              : <Settings2 key="scan-opts-open" className="w-4 h-4" />}
          </Button>
          {lastScan?.finished_at && (
            <span key="last-scan-info" className="text-[11px] text-slate-500" data-testid="fa-last-scan">
              Dernier scan : {String(lastScan.finished_at).slice(0, 16).replace('T', ' ')}
              {' · '}
              <b className={lastScan.imported > 0 ? 'text-emerald-700' : ''}>{lastScan.imported || 0}</b>
              {' importée(s)'}
            </span>
          )}
        </div>
      </div>

      {/* Barre de progression du push Pennylane groupé */}
      {pushBulkRunning && pushBulkProgress && (
        <div key="push-bulk-progress" className="bg-violet-50/80 border border-violet-200 rounded-lg p-4" data-testid="fa-push-bulk-progress">
          <div className="flex items-center justify-between text-xs text-slate-700 mb-1.5">
            <span className="font-medium truncate flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin text-violet-600" />
              {pushBulkProgress.step || 'Envoi Pennylane en cours…'}
              {pushBulkProgress.current_label && (
                <span className="text-slate-500 truncate max-w-[240px]"> · {pushBulkProgress.current_label}</span>
              )}
            </span>
            <span className="tabular-nums text-slate-500">
              {pushBulkProgress.processed || 0}/{pushBulkProgress.total || 0}
              {' · '}<b className="text-violet-700">{pushBulkProgress.pushed || 0}</b> poussée(s)
              {(pushBulkProgress.failed || 0) > 0 && <> · <b className="text-rose-700">{pushBulkProgress.failed}</b> ko</>}
            </span>
          </div>
          <div className="h-2 w-full bg-white/70 rounded-full border border-violet-200 overflow-hidden">
            <div
              className="h-full bg-violet-600 transition-all"
              style={{
                width: `${pushBulkProgress.total ? Math.round(((pushBulkProgress.processed || 0) / pushBulkProgress.total) * 100) : 5}%`,
              }}
            />
          </div>
          {Array.isArray(pushBulkProgress.errors) && pushBulkProgress.errors.length > 0 && (
            <details className="mt-2 text-[11px] text-rose-700">
              <summary className="cursor-pointer font-semibold">Voir les erreurs ({pushBulkProgress.errors.length})</summary>
              <ul className="mt-1 space-y-0.5 max-h-32 overflow-y-auto">
                {pushBulkProgress.errors.slice(-20).map((er, i) => (
                  <li key={i} className="truncate">• {er.label} — {er.error}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      {/* Barre de progression de l'insertion groupée */}
      {batching && batchProgress && (
        <div key="batch-progress" className="bg-emerald-50/80 border border-emerald-200 rounded-lg p-4" data-testid="fa-batch-progress">
          <div className="flex items-center justify-between text-xs text-slate-700 mb-1.5">
            <span className="font-medium truncate flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin text-emerald-600" />
              {batchProgress.step || 'Insertion groupée en cours…'}
              {batchProgress.current_file && (
                <span className="text-slate-500 truncate max-w-[220px]"> · {batchProgress.current_file}</span>
              )}
            </span>
            <span className="tabular-nums text-slate-500 flex items-center gap-2">
              <span>
                {batchProgress.processed || 0}/{batchProgress.total || 0}
                {' · '}<b className="text-emerald-700">{batchProgress.imported || 0}</b> ok
                {(batchProgress.failed || 0) > 0 && <> · <b className="text-rose-700">{batchProgress.failed}</b> ko</>}
              </span>
              <button
                type="button"
                onClick={async () => {
                  if (!window.confirm("Forcer l'arrêt de l'insertion groupée en cours ? À utiliser uniquement si le traitement semble bloqué.")) return;
                  try {
                    await api.post('/factures-achat/batch/reset');
                    setBatching(false);
                    setBatchProgress(null);
                    toast.success('Insertion groupée réinitialisée');
                    reload();
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || 'Impossible de réinitialiser');
                  }
                }}
                className="text-[11px] text-rose-600 hover:text-rose-700 underline font-medium"
                data-testid="fa-batch-reset-btn"
                title="Forcer l'arrêt si le traitement est bloqué"
              >
                Forcer l&apos;arrêt
              </button>
            </span>
          </div>
          <div className="h-2 w-full bg-white/70 rounded-full border border-emerald-200 overflow-hidden">
            <div
              className="h-full bg-emerald-600 transition-all"
              style={{
                width: `${batchProgress.total ? Math.round(((batchProgress.processed || 0) / batchProgress.total) * 100) : 5}%`,
              }}
            />
          </div>
          <p className="text-[11px] text-slate-500 mt-1.5">
            Le traitement tourne en arrière-plan. Vous pouvez quitter cette page, il continuera côté serveur.
          </p>
          {Array.isArray(batchProgress.errors) && batchProgress.errors.length > 0 && (
            <details className="mt-2 text-[11px] text-rose-700">
              <summary className="cursor-pointer font-semibold">Voir les erreurs ({batchProgress.errors.length})</summary>
              <ul className="mt-1 space-y-0.5 max-h-32 overflow-y-auto">
                {batchProgress.errors.slice(-20).map((er, i) => (
                  <li key={i} className="truncate">• {er.filename} — {er.error}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      {/* Barre de progression du scan IMAP en arrière-plan */}
      {scanning && scanProgress && (
        <div key="scan-progress" className="bg-indigo-50/80 border border-indigo-200 rounded-lg p-4" data-testid="fa-scan-progress">
          <div className="flex items-center justify-between text-xs text-slate-700 mb-1.5">
            <span className="font-medium truncate flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin text-indigo-600" />
              {scanProgress.step || 'Scan en cours…'}
            </span>
            <span className="tabular-nums text-slate-500">
              {scanProgress.imported || 0} importée(s) · {scanProgress.scanned_emails || 0} email(s)
            </span>
          </div>
          <div className="h-2 w-full bg-white/70 rounded-full border border-indigo-200 overflow-hidden">
            <div className="h-full bg-indigo-600 animate-pulse" style={{ width: '60%' }} />
          </div>
          <p className="text-[11px] text-slate-500 mt-1.5">
            Le scan tourne en arrière-plan. Vous pouvez quitter cette page, il continuera côté serveur.
          </p>
        </div>
      )}

      {/* Panneau d'options du scan */}
      {showScanOptions && (
        <div key="scan-options" className="bg-indigo-50/60 border border-indigo-200 rounded-lg p-4 -mt-1 space-y-3" data-testid="fa-scan-options">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-indigo-900">Options de scan IMAP</h3>
            <Button type="button" variant="ghost" size="sm" onClick={() => setShowScanOptions(false)}>
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-wider text-indigo-900/70 font-semibold mb-1">Depuis le</label>
              <Input
                type="date"
                value={sinceDate}
                onChange={(e) => setSinceDate(e.target.value)}
                data-testid="fa-scan-since-date"
              />
              <p className="text-[11px] text-slate-500 mt-1">Tous les emails reçus à partir de cette date seront scannés.</p>
            </div>
            <div className="space-y-2 pt-5">
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeRead}
                  onChange={(e) => setIncludeRead(e.target.checked)}
                  data-testid="fa-include-read"
                />
                <span><b>Inclure les emails déjà lus</b> (recommandé pour le 1er scan)</span>
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={markRead}
                  onChange={(e) => setMarkRead(e.target.checked)}
                  data-testid="fa-mark-read"
                />
                <span>Marquer les emails importés comme lus</span>
              </label>
            </div>
            <div className="pt-5">
              <div className="text-[11px] text-slate-600 bg-white/70 border border-indigo-200 rounded p-2">
                <p className="font-semibold text-indigo-900 mb-1">Astuce</p>
                <p>Pour rattraper les factures déjà ouvertes, cochez <b>« Inclure les emails déjà lus »</b> et choisissez une date plancher large (ex : 1ᵉʳ avril).</p>
              </div>
            </div>
          </div>
          <div className="flex justify-end">
            <Button
              onClick={handleScanEmails}
              disabled={scanning}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="fa-scan-with-options-btn"
            >
              {scanning
                ? <Loader2 key="scan-opt-load" className="w-4 h-4 mr-1.5 animate-spin" />
                : <Mail key="scan-opt-icon" className="w-4 h-4 mr-1.5" />}
              Lancer le scan
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <div key="loading" className="flex justify-center py-12"><Loader2 className="w-7 h-7 animate-spin text-indigo-600" /></div>
      ) : filtered.length === 0 ? (
        <div key="empty" className="bg-white rounded-2xl border border-slate-200 p-12 text-center" data-testid="fa-empty">
          <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">{search ? 'Aucune facture ne correspond' : "Glissez votre première facture fournisseur ci-dessus"}</p>
        </div>
      ) : (
        <div key="list" className="space-y-2">
          {/* Barre d'actions groupées + sélection globale */}
          <div
            className={`sticky top-0 z-10 flex items-center justify-between gap-3 px-3 py-2 rounded-lg border ${
              selectedIds.size > 0
                ? 'bg-indigo-50 border-indigo-300 shadow-sm'
                : 'bg-white border-slate-200'
            }`}
            data-testid="fa-bulk-bar"
          >
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => toggleSelectAll(filtered)}
                className="flex items-center gap-1.5 text-xs font-semibold text-slate-700 hover:text-indigo-700"
                data-testid="fa-select-all-btn"
              >
                {filtered.length > 0 && filtered.every((f) => selectedIds.has(f.facture_achat_id)) ? (
                  <CheckSquare className="w-4 h-4 text-indigo-600" />
                ) : (
                  <Square className="w-4 h-4 text-slate-400" />
                )}
                <span>
                  {selectedIds.size > 0
                    ? `${selectedIds.size} sélectionnée(s)`
                    : `Tout sélectionner (${filtered.length})`}
                </span>
              </button>
              {selectedIds.size > 0 && (
                <button
                  type="button"
                  onClick={clearSelection}
                  className="text-[11px] text-slate-500 underline hover:text-slate-700"
                  data-testid="fa-clear-selection"
                >
                  Effacer
                </button>
              )}
            </div>
            {selectedIds.size > 0 && (
              <div className="flex items-center gap-2" data-testid="fa-bulk-actions">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleBulkValidate}
                  disabled={bulkActionLoading !== null || pushBulkRunning}
                  className="border-blue-300 text-blue-700 hover:bg-blue-50"
                  data-testid="fa-bulk-validate-btn"
                >
                  {bulkActionLoading === 'validate'
                    ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                    : <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />}
                  Valider ({selectedIds.size})
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={handleBulkPushPennylane}
                  disabled={bulkActionLoading !== null || pushBulkRunning}
                  className="bg-violet-600 hover:bg-violet-700 text-white"
                  data-testid="fa-bulk-push-btn"
                >
                  {bulkActionLoading === 'push'
                    ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                    : <Send className="w-3.5 h-3.5 mr-1.5" />}
                  Pousser Pennylane
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleBulkDelete}
                  disabled={bulkActionLoading !== null || pushBulkRunning}
                  className="border-rose-300 text-rose-700 hover:bg-rose-50"
                  data-testid="fa-bulk-delete-btn"
                >
                  {bulkActionLoading === 'delete'
                    ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                    : <Trash2 className="w-3.5 h-3.5 mr-1.5" />}
                  Supprimer
                </Button>
              </div>
            )}
          </div>

          {filtered.map((f) => {
            const isSelected = selectedIds.has(f.facture_achat_id);
            return (
              <div
                key={f.facture_achat_id}
                className={`w-full bg-white rounded-xl border transition-all flex items-center gap-3 p-4 ${
                  isSelected
                    ? 'border-indigo-400 ring-1 ring-indigo-200 shadow-sm'
                    : 'border-slate-200 hover:border-indigo-300 hover:shadow-md'
                }`}
                data-testid={`fa-row-${f.facture_achat_id}`}
              >
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); toggleSelect(f.facture_achat_id); }}
                  className="shrink-0 p-1 -ml-1 rounded hover:bg-slate-100"
                  aria-label={isSelected ? 'Désélectionner' : 'Sélectionner'}
                  data-testid={`fa-row-select-${f.facture_achat_id}`}
                >
                  {isSelected
                    ? <CheckSquare className="w-5 h-5 text-indigo-600" />
                    : <Square className="w-5 h-5 text-slate-300" />}
                </button>
                <button
                  type="button"
                  onClick={() => setPreviewFacture(f)}
                  className="flex-1 flex items-center gap-3 min-w-0 text-left"
                  data-testid={`fa-row-open-${f.facture_achat_id}`}
                >
                  <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center shrink-0">
                    <FileText className="w-4 h-4 text-indigo-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-bold text-slate-900 truncate">{f.fournisseur?.nom || f.filename || '—'}</p>
                      <Badge className={STATUT_STYLE[f.statut] || 'bg-slate-100 text-slate-700'}>{f.statut}</Badge>
                      {f.pennylane_invoice_id && (
                        <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700">
                          Pennylane · {f.pennylane_invoice_id}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-slate-500 flex-wrap">
                      {f.numero_facture && <span className="inline-flex items-center gap-1"><Hash className="w-3 h-3" />{f.numero_facture}</span>}
                      {f.date_facture && <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{f.date_facture}</span>}
                      {f.fournisseur?.siret && <span className="inline-flex items-center gap-1"><Building2 className="w-3 h-3" />SIRET {f.fournisseur.siret}</span>}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-bold text-slate-900">{fmt(f.montants?.total_ttc || 0)} €</p>
                    <p className="text-xs text-slate-400">TTC</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Preview drawer — click on a row opens this side panel with PDF viewer + prev/next */}
      {previewFacture && (
        <FactureAchatPreviewDrawer
          facture={previewFacture}
          allFactures={filtered}
          onClose={() => setPreviewFacture(null)}
          onNavigate={(next) => setPreviewFacture(next)}
          onChanged={() => {
            reload().then(() => {
              // Rafraîchir la facture actuellement affichée depuis la nouvelle liste
              setPreviewFacture((cur) => {
                if (!cur) return null;
                // Cherche la version rechargée via l'items state (fera un update au prochain render)
                return cur; // laisse le useEffect suivant refresh depuis items
              });
            });
          }}
          onOpenDetail={(id) => navigate(`/factures-achat/${id}`)}
        />
      )}
    </div>
  );
}
